import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { registerDestructor } from '@ember/destroyable';
import { dropTask } from 'ember-concurrency';
import type RouterService from '@ember/routing/router-service';
import type IntlService from 'ember-intl/services/intl';
import type NotificationsService from 'tio-ui/services/notifications';
import {
  isReadSafeRequest,
  TioReadOnlyError,
  type BlockedWrite,
} from 'tio-data/handlers/read-only-handler';
import ENV from 'tio-employee/config/environment';
import type SessionService from 'tio-employee/services/session';
import type SessionContextService from 'tio-employee/services/session-context';
import {
  isPreviewBoot,
  isPreviewGrantMessage,
  landingRouteFor,
  parseExperience,
  parseSurface,
  previewHost,
  PREVIEW_MESSAGE,
  readPreviewBootMark,
  readPreviewClaim,
  writePreviewBootMark,
  type PreviewClaim,
  type PreviewExperience,
  type PreviewHost,
  type PreviewSurface,
} from 'tio-employee/utils/preview-handoff';

/** How long the hand-off waits for the host to answer `ready` with a grant. */
const GRANT_TIMEOUT_MS = 15_000;

/** One refused write toasts; a burst of them (a form that fires several saves) does not. */
const BLOCKED_TOAST_COOLDOWN_MS = 5_000;

export type PreviewFailure =
  'not_framed' | 'bad_surface' | 'no_grant' | 'bad_grant' | 'storage' | 'refused';

/** The read-only landing preview (RFC 128): takes the grant from the admin host, seats it, and keeps the app read-only after. */
export default class PreviewService extends Service {
  @service declare session: SessionService;
  @service declare sessionContext: SessionContextService;
  @service declare router: RouterService;
  @service declare notifications: NotificationsService;
  @service declare intl: IntlService;

  @tracked claim: PreviewClaim | null = null;
  @tracked surface: PreviewSurface | null = null;
  @tracked experience: PreviewExperience = 'current';
  @tracked expired = false;
  @tracked failure: PreviewFailure | null = null;

  /** Overridable so a test can stand in for the framing window. */
  resolveHost: () => PreviewHost | null = previewHost;

  /** The API origin the raw-fetch guard watches; a test points it somewhere it can prove. */
  apiHost: string = ENV.apiHost;

  /** The admin origins a grant is accepted from and `ready` is announced to; per build environment. */
  allowedHostOrigins: readonly string[] = ENV.previewHostOrigins ?? [];

  #host: PreviewHost | null = null;
  #hostOrigin: string | null = null;
  #lastBlockedToastAt = 0;

  constructor(owner: object) {
    // @ts-expect-error Service's constructor is typed without an owner parameter
    super(owner);
    if (isPreviewBoot()) {
      this.#installFetchGuard();
    }
  }

  /** Whether this page load is a preview, expired or not; every write stays refused once it is. */
  get isActive() {
    return this.claim !== null;
  }

  get isBeta() {
    return this.experience === 'beta';
  }

  get isLive() {
    return this.isActive && !this.expired;
  }

  /** Epoch milliseconds the grant stops verifying, from the session's decoded token. */
  get expiresAt() {
    return this.session.expireAt;
  }

  /** The hand-off a reload inside this frame should return to, or null outside a preview frame. */
  get bootMark() {
    return readPreviewBootMark();
  }

  /** The hand-off, once: grant from the host, seat it, load `/me`, land on the surface; a failure is shown, never retried. */
  start = dropTask(async (params: { surface?: unknown; experience?: unknown }) => {
    this.failure = null;
    this.expired = false;

    const surface = parseSurface(params.surface);
    if (!surface) {
      this.failure = 'bad_surface';
      return;
    }
    const experience = parseExperience(params.experience);

    const host = this.resolveHost();
    if (!host) {
      this.failure = 'not_framed';
      return;
    }

    let grant: { token: string; origin: string };
    try {
      grant = await this.#receiveGrant(host);
    } catch {
      this.failure = 'no_grant';
      return;
    }

    const claim = readPreviewClaim(grant.token);
    if (!claim || claim.surface !== surface) {
      this.failure = 'bad_grant';
      return;
    }
    // Marked only on a real grant; without a mark that reads back, a reload would boot the persistent store.
    if (!writePreviewBootMark({ surface, experience })) {
      this.failure = 'storage';
      return;
    }

    this.#host = host;
    this.#hostOrigin = grant.origin;
    this.claim = claim;
    this.surface = surface;
    this.experience = experience;

    try {
      await this.session.authenticate('authenticator:preview', { access_token: grant.token });
      await this.sessionContext.load();
    } catch {
      // The grant may already be seated: stay a preview (read-only, no renewal) and end it, never half-seated.
      this.failure = 'refused';
      this.expire();
      return;
    }

    this.#post({ type: PREVIEW_MESSAGE.started });
    this.router.replaceWith(landingRouteFor(surface));
  });

  /** Marks the grant dead and tells the host once; idempotent, and a no-op outside a preview. */
  expire = () => {
    if (!this.isActive || this.expired) {
      return;
    }
    this.expired = true;
    this.#post({ type: PREVIEW_MESSAGE.expired });
  };

  /** Surfaces a refused write as one toast per cooldown window; silent writes never toast. */
  noteBlockedWrite = (blocked: BlockedWrite) => {
    if (blocked.silent) {
      return;
    }
    const now = Date.now();
    if (now - this.#lastBlockedToastAt < BLOCKED_TOAST_COOLDOWN_MS) {
      return;
    }
    this.#lastBlockedToastAt = now;
    this.notifications.add(this.intl.t('preview.write_blocked'), { appearance: 'warning' });
  };

  /** Announces `ready` to each allowed origin and resolves with the first grant from the host window at one of them. */
  #receiveGrant(host: PreviewHost) {
    const allowed = this.allowedHostOrigins;

    return new Promise<{ token: string; origin: string }>((resolve, reject) => {
      const cleanup = () => {
        clearTimeout(timer);
        window.removeEventListener('message', onMessage);
      };
      const timer = setTimeout(() => {
        cleanup();
        reject(new Error('No preview grant received'));
      }, GRANT_TIMEOUT_MS);
      const onMessage = (event: MessageEvent) => {
        if (event.source !== host || !allowed.includes(event.origin)) {
          return;
        }
        if (!isPreviewGrantMessage(event.data)) {
          return;
        }
        cleanup();
        resolve({ token: event.data.token, origin: event.origin });
      };

      window.addEventListener('message', onMessage);
      registerDestructor(this, cleanup);
      for (const origin of allowed) {
        host.postMessage({ type: PREVIEW_MESSAGE.ready }, origin);
      }
    });
  }

  /** The request resolved against the page, with dot segments gone; null when it is not for the API's origin. */
  #apiTarget(raw: string) {
    try {
      const page = window.location.origin;
      const target = new URL(raw, page);
      return target.origin === new URL(this.apiHost || page, page).origin ? target : null;
    } catch {
      return null;
    }
  }

  #post(message: { type: string }) {
    if (this.#host && this.#hostOrigin) {
      this.#host.postMessage(message, this.#hostOrigin);
    }
  }

  /** Covers the raw `fetch` writers the store chain never sees; `/api/public/*` (telemetry) stays allowed. */
  #installFetchGuard() {
    if (typeof window === 'undefined') {
      return;
    }
    const original = window.fetch.bind(window);
    const guarded: typeof fetch = (input, init) => {
      if (this.isActive) {
        const raw =
          typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
        const method = (
          init?.method ??
          (input instanceof Request ? input.method : undefined) ??
          'GET'
        ).toUpperCase();
        const target = this.#apiTarget(raw);
        if (
          target &&
          !target.pathname.startsWith('/api/public/') &&
          !isReadSafeRequest(method, target.href, this.apiHost)
        ) {
          this.noteBlockedWrite({ method, url: target.href, silent: false });
          return Promise.reject(new TioReadOnlyError(method, target.href));
        }
      }
      return original(input, init);
    };
    window.fetch = guarded;
    registerDestructor(this, () => {
      if (window.fetch === guarded) {
        window.fetch = original;
      }
    });
  }
}

declare module '@ember/service' {
  interface Registry {
    preview: PreviewService;
  }
}
